import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Search, Ticket, Users, CornerDownLeft, StickyNote, Clock } from "lucide-react";
import { api } from "@/lib/api";
import { tourTitle, statusColor } from "@/lib/fields";
import { matchScore, substrAll } from "@/lib/fuzzy";

const searchable = (obj, skip = []) =>
  Object.entries(obj)
    .filter(([k]) => !skip.includes(k))
    .map(([, v]) => (v === null || v === undefined ? "" : typeof v === "object" ? JSON.stringify(v) : String(v)))
    .join(" ")
    .toLowerCase();

// Global search: trigger button + command dialog. Searches every field of every
// tour and guest profile across the whole app.
export const GlobalSearch = ({ variant = "icon" }) => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [tours, setTours] = useState([]);
  const [profiles, setProfiles] = useState([]);
  const [notes, setNotes] = useState([]);
  const [recents, setRecents] = useState([]);

  const load = useCallback(async () => {
    try {
      const [t, p, sh] = await Promise.all([api.listGuests(), api.listProfiles(), api.listShifts()]);
      setTours(t); setProfiles(p);
      setNotes((Array.isArray(sh) ? sh : []).filter((s) => s.note && String(s.note).trim()));
    } catch { /* ignore */ }
  }, []);

  const loadRecents = useCallback(() => {
    try { setRecents(JSON.parse(localStorage.getItem("sd:recentSearches") || "[]")); }
    catch { setRecents([]); }
  }, []);

  // Remember a search once the guide acts on a result — kept short, most-recent-first, no dupes.
  const rememberSearch = useCallback((term) => {
    const val = String(term || "").trim();
    if (!val) return;
    let next = [];
    try { next = JSON.parse(localStorage.getItem("sd:recentSearches") || "[]"); } catch { next = []; }
    next = [val, ...next.filter((x) => x.toLowerCase() !== val.toLowerCase())].slice(0, 6);
    localStorage.setItem("sd:recentSearches", JSON.stringify(next));
    setRecents(next);
  }, []);

  const clearRecents = () => { localStorage.removeItem("sd:recentSearches"); setRecents([]); };

  useEffect(() => { if (open) { load(); loadRecents(); setQuery(""); } }, [open, load, loadRecents]);

  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") { e.preventDefault(); setOpen(true); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const q = query.trim().toLowerCase();

  // Rank by relevance: name/title matches (typo-tolerant) rank above free-text body matches,
  // and each list is sorted best-first so the strongest hit is always on top.
  const guestNamesOf = (t) => [
    ...(Array.isArray(t.linked_guests) ? t.linked_guests : []),
    ...((Array.isArray(t.guest_list) ? t.guest_list : []).map((g) => (g && (g.name || (typeof g === "string" ? g : ""))))),
  ].filter(Boolean);
  const rank = (arr, scoreFn, limit) =>
    arr.map((x) => [scoreFn(x), x]).filter((p) => p[0] > 0).sort((a, b) => b[0] - a[0]).slice(0, limit).map((p) => p[1]);

  const scoreTour = (t) => {
    const nameText = [tourTitle(t), ...guestNamesOf(t)].join(" · ");
    const ns = matchScore(nameText, q);
    if (ns) return ns + 10; // name/guest matches always beat body-only matches
    const blob = searchable(t, ["tour_sheet_image", "guest_photos", "guest_list", "linked_guests", "notes"]);
    return substrAll(blob, q) ? 30 : 0;
  };
  const scoreProfile = (p) => {
    const ns = matchScore(p.name, q);
    if (ns) return ns + 10;
    return substrAll(searchable(p, ["tour_list"]), q) ? 25 : 0;
  };
  // Notes match on their content only (substring, no fuzzy) so long note bodies don't create
  // false positives — the "Riccio" search shouldn't surface unrelated tours.
  const scoreTourNote = (t) => (t.notes && String(t.notes).trim() && substrAll(t.notes, q) ? 60 : 0);
  const scoreShiftNote = (s) => (substrAll(`${s.label || ""} ${s.note || ""} ${s.date || ""}`, q) ? 60 : 0);

  const tourHits = q ? rank(tours, scoreTour, 12) : [];
  const profileHits = q ? rank(profiles, scoreProfile, 8) : [];
  const tourNoteHits = q ? rank(tours, scoreTourNote, 10) : [];
  const noteHits = q ? rank(notes, scoreShiftNote, 10) : [];

  const go = (path) => { rememberSearch(query); setOpen(false); navigate(path); };

  // Notes live on the calendar — open the calendar focused on that shift's date.
  const goDate = (dateStr) => {
    rememberSearch(query);
    setOpen(false);
    sessionStorage.setItem("sd:pendingCalendarDate", dateStr || "");
    window.dispatchEvent(new CustomEvent("sd:open-calendar-date", { detail: dateStr }));
    navigate("/");
  };

  const fmtDate = (mdy) => {
    const m = String(mdy || "").match(/(\d{1,2})\/(\d{1,2})\/(\d{2,4})/);
    if (!m) return mdy || "";
    const y = m[3].length === 2 ? "20" + m[3] : m[3];
    const d = new Date(+y, +m[1] - 1, +m[2]);
    return isNaN(d.getTime()) ? mdy : d.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric", year: "numeric" });
  };

  return (
    <>
      {variant === "icon" ? (
        <button
          onClick={() => setOpen(true)}
          data-testid="global-search-btn"
          className="hdr-chip flex h-9 w-9 items-center justify-center rounded-lg border transition-colors hover:text-white"
          aria-label="Search"
        >
          <Search className="h-4 w-4" />
        </button>
      ) : (
        <button
          onClick={() => setOpen(true)}
          data-testid="global-search-btn"
          className="flex w-full items-center gap-2 rounded-lg border border-zinc-700 bg-zinc-900/60 px-3 py-2.5 text-left text-sm text-zinc-400 transition-colors hover:border-zinc-600 hover:bg-zinc-800"
        >
          <Search className="h-4 w-4" /> Search tours, guests, anything…
          <kbd className="ml-auto hidden rounded bg-zinc-800 px-1.5 py-0.5 text-[10px] text-zinc-300 sm:inline">⌘K</kbd>
        </button>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="top-[15%] max-w-xl translate-y-0 gap-0 border-zinc-800 bg-zinc-950 p-0" data-testid="global-search-dialog">
          <DialogHeader className="sr-only">
            <DialogTitle>Search</DialogTitle>
            <DialogDescription>Search across all tours and guests</DialogDescription>
          </DialogHeader>
          <div className="flex items-center gap-2 border-b border-zinc-800 px-4">
            <Search className="h-4 w-4 text-zinc-400" />
            <Input
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search any field — name, date, city, tip, notes…"
              className="border-0 bg-transparent px-0 text-base focus-visible:ring-0"
              data-testid="global-search-input"
            />
          </div>

          <div className="max-h-[60vh] overflow-y-auto p-2">
            {!q && recents.length > 0 && (
              <div className="mb-1" data-testid="recent-searches">
                <div className="flex items-center justify-between px-3 py-1">
                  <span className="text-[10px] uppercase tracking-wider text-zinc-600">Recent searches</span>
                  <button onClick={clearRecents} data-testid="recent-clear-btn" className="text-[10px] uppercase tracking-wider text-zinc-500 transition-colors hover:text-zinc-300">Clear</button>
                </div>
                {recents.map((term) => (
                  <button
                    key={term}
                    onClick={() => setQuery(term)}
                    data-testid={`recent-search-${term}`}
                    className="flex w-full items-center gap-3 rounded-md px-3 py-2 text-left transition-colors hover:bg-white/5"
                  >
                    <Clock className="h-4 w-4 shrink-0 text-zinc-500" />
                    <span className="flex-1 truncate text-sm text-zinc-200">{term}</span>
                    <CornerDownLeft className="h-3.5 w-3.5 text-zinc-700" />
                  </button>
                ))}
              </div>
            )}
            {!q && recents.length === 0 && <p className="px-3 py-6 text-center text-sm text-zinc-400">Type to search every tour and guest.</p>}
            {q && tourHits.length === 0 && profileHits.length === 0 && noteHits.length === 0 && tourNoteHits.length === 0 && (
              <p className="px-3 py-6 text-center text-sm text-zinc-400">No matches for “{query}”.</p>
            )}

            {tourHits.length > 0 && (
              <div className="mb-2">
                <div className="px-3 py-1 text-[10px] uppercase tracking-wider text-zinc-600">Tours</div>
                {tourHits.map((t) => (
                  <button
                    key={t.id}
                    onClick={() => go(`/guest/${t.id}`)}
                    data-testid={`search-tour-${t.id}`}
                    className="flex w-full items-center gap-3 rounded-md px-3 py-2 text-left transition-colors hover:bg-white/5"
                  >
                    <Ticket className="h-4 w-4 shrink-0 text-[#E0575B]" />
                    <span className="flex-1 truncate text-sm text-zinc-100">{tourTitle(t)}</span>
                    {t.booking_status && <span className={`rounded-full px-2 py-0.5 text-[10px] ${statusColor(t.booking_status)}`}>{t.booking_status}</span>}
                    <CornerDownLeft className="h-3.5 w-3.5 text-zinc-700" />
                  </button>
                ))}
              </div>
            )}

            {profileHits.length > 0 && (
              <div className="mb-2">
                <div className="px-3 py-1 text-[10px] uppercase tracking-wider text-zinc-600">Guests</div>
                {profileHits.map((p) => (
                  <button
                    key={p.name}
                    onClick={() => go(`/profile/${encodeURIComponent(p.name)}`)}
                    data-testid={`search-profile-${p.name}`}
                    className="flex w-full items-center gap-3 rounded-md px-3 py-2 text-left transition-colors hover:bg-white/5"
                  >
                    <Users className="h-4 w-4 shrink-0 text-zinc-400" />
                    <span className="flex-1 truncate text-sm text-zinc-100">{p.name}</span>
                    <span className="text-xs text-zinc-400">{p.lifetime_tours} tour{p.lifetime_tours === 1 ? "" : "s"}</span>
                    <CornerDownLeft className="h-3.5 w-3.5 text-zinc-700" />
                  </button>
                ))}
              </div>
            )}

            {tourNoteHits.length > 0 && (
              <div className="mb-2">
                <div className="px-3 py-1 text-[10px] uppercase tracking-wider text-zinc-600">Tour notes</div>
                {tourNoteHits.map((t) => (
                  <button
                    key={t.id}
                    onClick={() => go(`/guest/${t.id}`)}
                    data-testid={`search-tour-note-${t.id}`}
                    className="flex w-full items-start gap-3 rounded-md px-3 py-2 text-left transition-colors hover:bg-white/5"
                  >
                    <StickyNote className="mt-0.5 h-4 w-4 shrink-0 text-[#E0575B]" />
                    <span className="min-w-0 flex-1">
                      <span className="flex items-center gap-2">
                        <span className="truncate text-sm text-zinc-100">{tourTitle(t)}</span>
                        {t.booking_status && <span className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] ${statusColor(t.booking_status)}`}>{t.booking_status}</span>}
                      </span>
                      <span className="mt-0.5 block truncate text-xs text-amber-300/90">{t.notes}</span>
                    </span>
                    <CornerDownLeft className="mt-0.5 h-3.5 w-3.5 shrink-0 text-zinc-700" />
                  </button>
                ))}
              </div>
            )}

            {noteHits.length > 0 && (
              <div className="mb-2">
                <div className="px-3 py-1 text-[10px] uppercase tracking-wider text-zinc-600">Shift notes</div>
                {noteHits.map((s) => (
                  <button
                    key={s.id}
                    onClick={() => goDate(s.date)}
                    data-testid={`search-note-${s.id}`}
                    className="flex w-full items-start gap-3 rounded-md px-3 py-2 text-left transition-colors hover:bg-white/5"
                  >
                    <StickyNote className="mt-0.5 h-4 w-4 shrink-0 text-amber-400" />
                    <span className="min-w-0 flex-1">
                      <span className="flex items-center gap-2">
                        <span className="truncate text-sm text-zinc-100">{s.label || "Shift"}</span>
                        <span className="shrink-0 text-xs text-zinc-500">{fmtDate(s.date)}</span>
                      </span>
                      <span className="mt-0.5 block truncate text-xs text-amber-300/90">{s.note}</span>
                    </span>
                    <CornerDownLeft className="mt-0.5 h-3.5 w-3.5 shrink-0 text-zinc-700" />
                  </button>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
